import {
  Box,
  Typography,
  Skeleton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { usePaginationState } from "../../../../hooks/usePaginationState";
import PaginationControls from "../../../shared/PaginationControls";
import { FaChevronDown, FaChevronUp, FaRegClock, FaPencilAlt, FaRobot } from "react-icons/fa";

type PropTypes = {
  data: any[];
  isLoading?: boolean;
  isError?: boolean;
  selectedUserName?: string | null;
  /** Diferenciação visual (pills, cores, ícones) — ex.: modal Dados Cadastrais (área project). */
  differentiateEvents?: boolean;
  /** Substitui o texto padrão quando não há registros. */
  emptyMessage?: string;
  /** Altura máxima da área rolável da lista. */
  maxHeight?: string | number;
  /** Lista cresce para preencher o pai (modal em coluna flex). */
  fillParent?: boolean;
};

const EXCLUDED_FIELDS = [
  "Criado em",
  "Atualizado em",
  "createdAt",
  "updatedAt",
  "_id",
  "Briefing",
  "__v",
  "deletedAt",
  "Deletado em",
  "Último usuário que atualizou",
  "isApiUpdate",
  "Atualização de API",
  "actionType",
  "budgetId",
  "ID do Orçamento",
  "budgetName",
  "Nome do Orçamento",
  "negotiationVersion",
  "Versão de negociação",
  "closingVersion",
  "Versão de fechamento",
  "serviceChanges",
  "Alterações de serviços",
  "serviceFieldChanges",
];

const AUD = {
  primary: "#1361A4",
  statusGreen: "#2e7d32",
  statusGreenBg: "#e8f5e9",
  statusGreenBgHover: "#dcedc8",
  fieldBlue: "#1361A4",
  fieldBlueBg: "#E3F2FD",
  fieldBlueBgHover: "#BBDEFB",
  iconGreen: "#2e7d32",
  iconBlue: "#1361A4",
  iconAmber: "#ed6c02",
  pillBorderInactive: "1px solid #CFD8DC",
  pillBorderActive: "2px solid #1361A4",
  chipGrey: "#eceff1",
  chipGreyText: "#37474f",
};

const MAX_SUPPLIER_LENGTH = 30;
const truncateSupplier = (name: string) => {
  if (!name) return "-";
  return name.length > MAX_SUPPLIER_LENGTH
    ? name.substring(0, MAX_SUPPLIER_LENGTH) + "..."
    : name;
};

const FRIENDLY_KEYS = ["label", "Nome", "name", "descricao", "description", "title", "valor", "value"];

const actionDict: { [key: string]: string } = {
  Update: "atualizou",
  Create: "criou",
  Delete: "excluiu",
  StatusChange: "alterou o status",
  DUPLICATE: "duplicou",
  DELETE: "excluiu",
  DELETED: "excluiu",
  RENAME: "renomeou",
  NEGOTIATE: "enviou para negociação",
  SEND_TO_NEGOTIATION: "enviou para negociação",
  APPROVED: "aprovou",
  NEGOTIATION_APPROVED: "aprovou negociação",
  DENIED: "reprovou",
  NEGOTIATION_DENIED: "reprovou negociação",
  NEW_VERSION: "criou nova versão",
  NEGOTIATION_VERSION_CREATED: "criou nova versão de negociação",
  NEGOTIATION_UPDATED: "atualizou negociação",
  APPROVE: "enviou para fechamento",
  APROVE: "aprovou para fechamento",
  SEND_TO_CLOSING: "enviou para fechamento",
  CLOSING_CREATED: "criou fechamento",
  CLOSING_UPDATED: "atualizou fechamento",
  CLOSING_APPROVED: "aprovou fechamento",
  CLOSING_DENIED: "reprovou fechamento",
  CLOSING_VERSION_CREATED: "criou nova versão de fechamento",
  CLOSING_FINALIZED: "finalizou fechamento",
  EXTRAS_AUTO_ZEROED: "zerou automaticamente a previsão de extras",
  PRE_CLOSING: "enviou para pré-fechamento",
  CLOSING_SENT_TO_PRE_CLOSING: "enviou para pré-fechamento",
  RETURNED_FOR_ADJUSTMENTS: "devolveu para ajustes",
  CLOSING_RETURNED_FOR_ADJUSTMENTS: "devolveu para ajustes",
  SAVE: "salvou",
  VersionCreated: "criou nova versão",
  ServiceUpdate: "atualizou serviços",
  NegotiationServiceUpdate: "atualizou serviços de negociação",
  ClosingServiceUpdate: "atualizou serviços de fechamento",
  PaymentSolicitationUpdate: "atualizou solicitação de pagamento",
  NegotiationPaymentSolicitationUpdate: "atualizou solicitação de pagamento",
  ClosingPaymentSolicitationUpdate: "atualizou solicitação de pagamento",
  FilesUpdate: "atualizou arquivos",
  NegotiationFilesUpdate: "atualizou arquivos",
  ClosingFilesUpdate: "atualizou arquivos",
  Export: "exportou",
};

const truncateText = (text: string) => text;

const formatObject = (obj: any): string => {
  if (!obj) return "(Vazio)";

  for (const key of FRIENDLY_KEYS) {
    if ((obj as any)[key]) return truncateText(String((obj as any)[key]));
  }

  const parts = Object.entries(obj)
    .filter(([key, val]) => !EXCLUDED_FIELDS.includes(key) && val !== undefined)
    .map(([key, val]) => `${key}: ${formatPrimitive(val)}`);

  const combined = parts.join("\n");
  return truncateText(combined || "[Objeto]");
};

const formatPrimitive = (value: any): string => {
  if (value === null || value === undefined) return "(Vazio)";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number" || typeof value === "string") {
    const str = String(value);
    const normalizedNewlines = str.replace(/\\n/g, "\n").replace(/\r\n/g, "\n");

    const trimmed = normalizedNewlines.trim();
    if (trimmed === "") return "(Vazio)";

    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        return formatPrimitive(parsed);
      } catch {
        // mantém como string caso não seja JSON válido
      }
    }

    return normalizedNewlines;
  }
  if (Array.isArray(value)) {
    const mapped = value.map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return `• ${formatObject(item)}`;
      }
      return `• ${formatPrimitive(item)}`;
    });
    return mapped.join("\n");
  }
  if (typeof value === "object") {
    return formatObject(value);
  }
  return String(value);
};

const formatValue = (value: any): string => formatPrimitive(value);

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "---";
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "---";
  }
};

const formatTime = (dateStr: string): string => {
  if (!dateStr) return "---";
  try {
    const date = new Date(dateStr);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "---";
  }
};

function isStatusFieldName(field: string): boolean {
  const n = String(field).toLowerCase();
  return n === "status" || field === "Status";
}

function hasServiceFieldChangesInEntry(entry: any): boolean {
  const arr = entry.data?.serviceFieldChanges;
  return Array.isArray(arr) && arr.length > 0;
}

function allMeaningfulKeys(entry: any): string[] {
  const data = entry.data || {};
  const oldData = entry.oldData || {};
  const keys = new Set<string>();
  for (const k of Object.keys(data)) {
    if (!EXCLUDED_FIELDS.includes(k)) keys.add(k);
  }
  for (const k of Object.keys(oldData)) {
    if (!EXCLUDED_FIELDS.includes(k)) keys.add(k);
  }
  return Array.from(keys);
}

function isPureStatusAuditEntry(entry: any): boolean {
  if (entry.action === "StatusChange") return true;
  if (entry.action !== "Update" || hasServiceFieldChangesInEntry(entry)) return false;
  const keys = allMeaningfulKeys(entry).filter((k) => k !== "serviceFieldChanges");
  if (keys.length === 0) return false;
  return keys.every((k) => isStatusFieldName(k));
}

function matchesStatusFilterEntry(entry: any): boolean {
  if (entry.action === "StatusChange") return true;
  if (entry.action === "Update") {
    return allMeaningfulKeys(entry).some((k) => isStatusFieldName(k));
  }
  return false;
}

function matchesFieldFilterEntry(entry: any): boolean {
  if (entry.action === "StatusChange") return false;
  if (isPureStatusAuditEntry(entry)) return false;
  if (entry.action === "Create") return true;
  if (entry.action === "Update") {
    if (hasServiceFieldChangesInEntry(entry)) return true;
    return allMeaningfulKeys(entry).length > 0;
  }
  return true;
}

function isMiddRobotEntry(entry: any): boolean {
  const data = entry.data || {};
  if (data.isApiUpdate === true) return true;
  if (data["Atualização de API"] === true || data["Atualização de API"] === "Sim") return true;
  const name = String(entry.user?.name || "").toLowerCase();
  const email = String(entry.user?.email || "").toLowerCase();
  return name.includes("mmidd") || name.includes("robô mm") || name.includes("robo mm") || email.includes("mmidd");
}

function getStatusTransitionParts(fieldChanges: any[]): { from: string; to: string } | null {
  if (!fieldChanges?.length || fieldChanges[0]?.isServiceChange) return null;
  const row = fieldChanges.find((c) => isStatusFieldName(String(c.field)));
  if (!row) return null;
  return { from: String(row.oldValue), to: String(row.newValue) };
}

function countNonStatusFieldChanges(fieldChanges: any[]): number {
  if (!fieldChanges.length) return 0;
  if (fieldChanges[0]?.isServiceChange) return fieldChanges.length;
  return fieldChanges.filter((c) => !isStatusFieldName(String(c.field))).length;
}

function expandedHeaderUsesStatusStyle(entry: any): boolean {
  return isPureStatusAuditEntry(entry);
}

const ChangesList: React.FC<PropTypes> = ({
  data,
  isLoading,
  isError,
  selectedUserName,
  differentiateEvents = false,
  emptyMessage,
  maxHeight = "50vh",
  fillParent = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [auditFilter, setAuditFilter] = useState<"all" | "status" | "fields">("all");

  const auditEntries = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      const dateA = new Date(a.date || a.createdAt).getTime();
      const dateB = new Date(b.date || b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [data]);

  const filteredEntries = useMemo(() => {
    if (!differentiateEvents) return auditEntries;
    if (auditFilter === "all") return auditEntries;
    if (auditFilter === "status") return auditEntries.filter(matchesStatusFilterEntry);
    return auditEntries.filter(matchesFieldFilterEntry);
  }, [auditEntries, auditFilter, differentiateEvents]);

  /** Modal Dados Cadastrais: todos os itens expandidos ao abrir / ao mudar filtro ou dados. */
  useEffect(() => {
    if (!differentiateEvents) {
      setExpandedRows(new Set());
      return;
    }
    setExpandedRows(new Set(filteredEntries.map((_, i) => i)));
  }, [differentiateEvents, filteredEntries, auditFilter]);

  const {
    page,
    rowsPerPage,
    paginatedData: paginatedEntries,
    totalPages,
    rangeLabel,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginationState(filteredEntries);

  const countChangedFields = (entry: any): number => {
    const entryData = entry.data || {};
    if (Array.isArray(entryData.serviceFieldChanges) && entryData.serviceFieldChanges.length > 0) {
      return entryData.serviceFieldChanges.length;
    }
    return Object.keys(entryData).filter((key) => !EXCLUDED_FIELDS.includes(key)).length;
  };

  const getFieldChanges = (entry: any) => {
    const changes: any[] = [];
    const newData = entry.data || {};
    const oldData = entry.oldData || {};
    const dateStr = entry.date || entry.createdAt || "";

    if (Array.isArray(newData.serviceFieldChanges) && newData.serviceFieldChanges.length > 0) {
      for (const sfc of newData.serviceFieldChanges) {
        changes.push({
          isServiceChange: true,
          service: sfc.serviceType || "-",
          supplier: sfc.supplierName || "",
          field: sfc.field || "",
          oldValue: formatValue(sfc.oldValue),
          newValue: formatValue(sfc.newValue),
          time: formatTime(dateStr),
        });
      }
      return changes;
    }

    for (const key of Object.keys(newData)) {
      if (EXCLUDED_FIELDS.includes(key)) continue;
      const oldValue = oldData[key];
      const newValue = newData[key];
      changes.push({
        field: key,
        oldValue: formatValue(oldValue),
        newValue: formatValue(newValue),
        time: formatTime(dateStr),
      });
    }

    for (const key of Object.keys(oldData)) {
      if (EXCLUDED_FIELDS.includes(key)) continue;
      if (!(key in newData)) {
        changes.push({
          field: key,
          oldValue: formatValue(oldData[key]),
          newValue: "(Removido)",
          time: formatTime(dateStr),
        });
      }
    }

    return changes;
  };

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  /** Pills no estilo do mock: ativo = borda azul forte + negrito; inativo = transparente + borda leve. */
  const pillButtonSx = (active: boolean) =>
    active
      ? {
          px: 2,
          py: 0.75,
          borderRadius: "999px",
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          bgcolor: "transparent",
          color: AUD.primary,
          border: AUD.pillBorderActive,
        }
      : {
          px: 2,
          py: 0.75,
          borderRadius: "999px",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          bgcolor: "transparent",
          color: "#455a64",
          border: AUD.pillBorderInactive,
        };

  const renderListContent = () => {
    if (isLoading) {
      return (
        <Box p={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      );
    }

    if (isError) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="error">Erro ao carregar dados de auditoria. Tente novamente.</Typography>
        </Box>
      );
    }

    if (paginatedEntries.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="textSecondary">
            {emptyMessage ??
              `Nenhum registro encontrado${selectedUserName ? ` para ${selectedUserName}` : ""}.`}
          </Typography>
        </Box>
      );
    }

    return paginatedEntries.map((entry, index) => {
      const globalIndex = (page - 1) * rowsPerPage + index;
      const isExpanded = expandedRows.has(globalIndex);
      const fieldCount = countChangedFields(entry);
      const actionVerb = actionDict[entry.action] || entry.action;
      const dateFormatted = formatDate(entry.date || entry.createdAt || "");
      const userName = entry.user?.name || "Usuário desconhecido";
      const fieldChanges = getFieldChanges(entry);
      const robot = differentiateEvents && isMiddRobotEntry(entry);
      const statusStyleRow = differentiateEvents && expandedHeaderUsesStatusStyle(entry);
      const transition = differentiateEvents ? getStatusTransitionParts(fieldChanges) : null;
      const nonStatusCount = countNonStatusFieldChanges(fieldChanges);
      const showStatusTransitionPill = Boolean(differentiateEvents && transition && matchesStatusFilterEntry(entry));
      const pureStatus = differentiateEvents && isPureStatusAuditEntry(entry);
      const fieldEventVerb = differentiateEvents && !pureStatus && (entry.action === "Update" || hasServiceFieldChangesInEntry(entry));

      const displayUserName =
        differentiateEvents && robot ? entry.user?.name || "Robô MMIDD" : userName;

      const headerBg = (() => {
        if (!differentiateEvents) {
          return isExpanded ? "#F0F7FF" : "transparent";
        }
        if (!isExpanded) return "transparent";
        return statusStyleRow ? AUD.statusGreenBg : AUD.fieldBlueBg;
      })();

      const headerHoverBg = (() => {
        if (!differentiateEvents) return "#F0F7FF";
        return statusStyleRow ? AUD.statusGreenBgHover : AUD.fieldBlueBgHover;
      })();

      const chevronColor = differentiateEvents ? (statusStyleRow && isExpanded ? AUD.iconGreen : AUD.iconBlue) : "#1361A4";

      return (
        <Box
          key={entry._id || globalIndex}
          sx={{
            borderBottom: "1px solid #E0E0E0",
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Box
            onClick={() => toggleRow(globalIndex)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              cursor: "pointer",
              bgcolor: headerBg,
              "&:hover": { bgcolor: headerHoverBg },
              transition: "background-color 0.2s",
            }}
            role="button"
            aria-expanded={isExpanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                toggleRow(globalIndex);
                e.preventDefault();
              }
            }}
          >
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap" flex={1} minWidth={0}>
              {differentiateEvents && (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: robot ? "rgba(237,108,2,0.12)" : pureStatus || entry.action === "StatusChange" ? "rgba(46,125,50,0.12)" : "rgba(19,97,164,0.1)",
                    color: robot ? AUD.iconAmber : pureStatus || entry.action === "StatusChange" ? AUD.iconGreen : AUD.iconBlue,
                  }}
                  aria-hidden
                >
                  {robot ? (
                    <FaRobot size={12} />
                  ) : pureStatus || entry.action === "StatusChange" ? (
                    <FaRegClock size={12} />
                  ) : (
                    <FaPencilAlt size={11} />
                  )}
                </Box>
              )}
              {isExpanded ? <FaChevronUp color={chevronColor} size={12} /> : <FaChevronDown color="#666" size={12} />}
              <Typography variant="body2" component="span" sx={{ display: "inline", flexWrap: "wrap" }}>
                <strong>{displayUserName}</strong>{" "}
                {differentiateEvents ? (
                  <>
                    {pureStatus || entry.action === "StatusChange" ? (
                      <Typography component="span" sx={{ color: AUD.statusGreen, fontWeight: 600 }}>
                        alterou o status
                      </Typography>
                    ) : fieldEventVerb ? (
                      <Typography component="span" sx={{ color: AUD.fieldBlue, fontWeight: 600 }}>
                        atualizou {nonStatusCount > 0 ? nonStatusCount : fieldCount} campo
                        {(nonStatusCount > 0 ? nonStatusCount : fieldCount) !== 1 ? "s" : ""}
                      </Typography>
                    ) : (
                      <Typography component="span" sx={{ color: AUD.fieldBlue, fontWeight: 600 }}>
                        {actionVerb}
                      </Typography>
                    )}
                    {showStatusTransitionPill && transition && !isExpanded && (
                      <Chip
                        size="small"
                        label={`${transition.from} → ${transition.to}`}
                        sx={{
                          ml: 1,
                          verticalAlign: "middle",
                          bgcolor: AUD.statusGreenBg,
                          color: AUD.statusGreen,
                          fontWeight: 600,
                          border: `1px solid ${AUD.statusGreen}`,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )}
                    {!pureStatus && entry.action !== "StatusChange" && fieldCount > 0 && !fieldEventVerb && (
                      <>
                        {" "}
                        <strong>{fieldCount}</strong> campo{fieldCount > 1 ? "s" : ""}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Typography component="span" sx={{ color: "#1361A4", fontWeight: 500 }}>
                      {actionVerb}
                    </Typography>{" "}
                    {fieldCount > 0 && (
                      <>
                        <strong>{fieldCount}</strong> campo{fieldCount > 1 ? "s" : ""}
                      </>
                    )}
                  </>
                )}{" "}
                {differentiateEvents ? "" : <>em {dateFormatted}</>}
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ flexShrink: 0, pl: 1, whiteSpace: "nowrap" }}>
              {differentiateEvents
                ? `${dateFormatted} · ${formatTime(entry.date || entry.createdAt || "")}`
                : formatTime(entry.date || entry.createdAt || "")}
            </Typography>
          </Box>

          <Collapse in={isExpanded}>
            <Box
              sx={{
                px: 2,
                pb: 2,
                bgcolor: "#FFF",
                borderTop: "1px dashed #CCC",
                overflowX: "auto",
                maxHeight: fillParent ? "min(55vh, 520px)" : 320,
                overflowY: "auto",
              }}
            >
              {fieldChanges.length > 0 && fieldChanges[0].isServiceChange ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Serviço</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Fornecedor</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "16%" }}>Campo</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Alterado de</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "18%" }}>Para</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "12%" }}>Alterado às</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fieldChanges.map((change: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                          {change.service || "-"}
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                          <Tooltip title={change.supplier || ""} arrow placement="top">
                            <span>{truncateSupplier(change.supplier)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                          {change.field}
                        </TableCell>
                        <TableCell
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            color: change.oldValue === "(Vazio)" ? "#999" : "inherit",
                          }}
                        >
                          {change.oldValue}
                        </TableCell>
                        <TableCell
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            fontWeight: differentiateEvents ? 700 : 400,
                          }}
                        >
                          {change.newValue}
                        </TableCell>
                        <TableCell>{change.time}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "25%" }}>Campo</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Alterado de</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Para</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%" }}>Alterado às</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fieldChanges.map((change: any, idx: number) => {
                      const isStatusRow =
                        differentiateEvents &&
                        !fieldChanges[0]?.isServiceChange &&
                        isStatusFieldName(String(change.field)) &&
                        matchesStatusFilterEntry(entry);
                      return (
                        <TableRow
                          key={idx}
                          sx={isStatusRow ? { bgcolor: AUD.statusGreenBg, "& td": { borderColor: "rgba(46,125,50,0.2)" } } : undefined}
                        >
                          <TableCell
                            sx={{
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                              fontWeight: isStatusRow ? 700 : 400,
                            }}
                          >
                            {change.field}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                            {isStatusRow ? (
                              <Chip
                                label={change.oldValue}
                                size="small"
                                sx={{
                                  bgcolor: AUD.chipGrey,
                                  color: AUD.chipGreyText,
                                  fontWeight: 500,
                                  height: "auto",
                                  "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                                }}
                              />
                            ) : (
                              <Box
                                component="span"
                                sx={{
                                  color: change.oldValue === "(Vazio)" ? "#999" : "inherit",
                                }}
                              >
                                {change.oldValue}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                            {isStatusRow ? (
                              <Chip
                                label={change.newValue}
                                size="small"
                                sx={{
                                  bgcolor: AUD.statusGreenBg,
                                  color: AUD.statusGreen,
                                  fontWeight: 600,
                                  border: `1px solid ${AUD.statusGreen}`,
                                  height: "auto",
                                  "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                                }}
                              />
                            ) : (
                              <Box
                                component="span"
                                sx={{ fontWeight: differentiateEvents ? 700 : 400 }}
                              >
                                {change.newValue}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{change.time}</TableCell>
                        </TableRow>
                      );
                    })}
                    {fieldChanges.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            Nenhum detalhe de campo disponível
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </Box>
      );
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: fillParent ? "100%" : "auto",
        minHeight: fillParent ? 0 : undefined,
        flex: fillParent ? 1 : undefined,
        overflow: "hidden",
      }}
    >
      {differentiateEvents && (
        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          <Box
            component="button"
            type="button"
            aria-pressed={auditFilter === "all"}
            onClick={() => setAuditFilter("all")}
            sx={pillButtonSx(auditFilter === "all")}
          >
            Todos os eventos
          </Box>
          <Box
            component="button"
            type="button"
            aria-pressed={auditFilter === "status"}
            onClick={() => setAuditFilter("status")}
            sx={pillButtonSx(auditFilter === "status")}
          >
            Alterações de status
          </Box>
          <Box
            component="button"
            type="button"
            aria-pressed={auditFilter === "fields"}
            onClick={() => setAuditFilter("fields")}
            sx={pillButtonSx(auditFilter === "fields")}
          >
            Alterações de campos
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: fillParent ? 0 : 200,
          overflowY: "auto",
          border: "1px solid #E0E0E0",
          borderRadius: 1,
          bgcolor: "#FAFAFA",
          ...(fillParent ? { maxHeight: "none" } : { maxHeight }),
        }}
      >
        {renderListContent()}
      </Box>

      {filteredEntries.length > 0 && (
        <Box sx={{ flexShrink: 0, pt: fillParent ? 1 : 0 }}>
          <PaginationControls
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            rangeLabel={rangeLabel}
            onPageChange={(e, value) => {
              handlePageChange(e, value);
            }}
            onRowsPerPageChange={(value) => {
              handleRowsPerPageChange(value);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ChangesList;
